import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import {
  BagIcon, // 袋子圖標
  WaterIcon, // 水圖標
  SettingIcon, // 設定圖標
  BoltIcon, // 閃電圖標
  ArrowIcon, // 箭頭圖標
  Plus, // 加號圖標
  Trash2, // 垃圾桶圖標
} from "./constants/icons";
import { useSelector } from "react-redux";
import { selectCurrentCategory } from "../../store/settingSlice";
// 可排序的樹節點組件
export default function SortableTreeNode({
  node,
  level = 0,
  onToggleExpand,
  isNodeExpanded,
  onAddChild,
  onDeleteNode,
  onEditNode,
  activeId,
  overId,
  isRoot = false, // 新增 isRoot 參數
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    setDroppableNodeRef,
  } = useSortable({
    id: node.id,
    data: {
      type: "tree-node",
      node: node,
      level: level,
    },
    disabled: level === 0, // 🔑 禁用第一層節點的拖拽功能
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const iconMapping = {
    bolt: BoltIcon,
    water: WaterIcon,
    setting: SettingIcon,
    bag: BagIcon,
  };
  // 根據 category_id 從currentCategory 獲取對應的圖標文字再去iconMapping找到對應圖標
  const currentCategory = useSelector(selectCurrentCategory);
  const category = currentCategory?.find(
    (c) => c.category_id === node.category_id
  );
  const Icon = iconMapping[category?.icon] || BagIcon; // 使用預設圖標作為後備
  const hasChildren = node.children && node.children.length > 0;
  const isActiveDropTarget = isOver && activeId !== node.id;
  const isFirstLevel = level === 0; // 🔑 判斷是否為第一層
  const fieldLabels = {
    architecture: "建築位置:",
    endDevice: "終端設備:",
    voltage: "電壓V:",
    ampere: "斷路器規格A:",
    phases: "相數:",
  };
  return (
    <div ref={setNodeRef} style={style} className="w-full">
      <div
        ref={setDroppableNodeRef}
        {...attributes}
        className={`
            p-3 sm:p-4 mb-2 rounded-lg border-2 w-full min-w-fit
            ${isDragging ? "border-blue-500 bg-blue-500/20 scale-105" : ""} 
            ${
              isActiveDropTarget
                ? "border-green-500 bg-green-500/20 ring-2 ring-green-500/50"
                : ""
            } 
            ${isFirstLevel ? "cursor-default" : "cursor-move"} 
            transition-all duration-200 hover:shadow-lg
            relative bg-[#1A111DB3]
          `}
        style={{
          marginLeft: `${level * 16}px`,
          borderColor: category?.color || "#6B7280", // 使用內聯樣式設定邊框顏色
        }}
      >
        {/* 拖拽手柄區域 - 第一層不顯示 */}
        {!isFirstLevel && (
          <div
            {...listeners}
            className="absolute left-2 top-2 bottom-2 w-2 opacity-30 hover:opacity-60 cursor-grab active:cursor-grabbing rounded"
            style={{
              background:
                "repeating-linear-gradient(0deg, transparent, transparent 2px, currentColor 2px, currentColor 4px)",
            }}
          />
        )}

        {/* 標題列（展開按鈕 + 名稱 + 操作按鈕） */}
        <div
          className={`flex items-center gap-2 sm:gap-4 ${
            isFirstLevel ? "" : "ml-4"
          }`}
        >
          {hasChildren ? (
            <button
              onClick={() => onToggleExpand(node.id)}
              className="flex-shrink-0 p-1 hover:bg-white/10 rounded"
            >
              <ArrowIcon isExpanded={isNodeExpanded(node.id)} />{" "}
            </button>
          ) : (
            <div className="w-4 h-4" />
          )}

          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-[200px]">
            <div className="text-xs sm:text-sm font-medium truncate">
              {category?.category_name || "未分類"}
            </div>
            <div className="text-xs text-[#CCBBBB] truncate border-l-2 pl-1 border-[#CCBBBB]">
              {node.name}
            </div>
            <div className="flex items-center justify-center w-8 h-8 p-1 rounded-full bg-[#6D6A6A2A]">
              <Icon />
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => onAddChild(node.id)}
              className="p-1.5 sm:p-2 bg-green-600 hover:bg-green-700 rounded text-white transition-colors"
              title="新增子節點"
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => onEditNode(node)}
              className="p-1.5 sm:p-2 bg-blue-600 hover:bg-blue-700 rounded text-white transition-colors"
              title="編輯"
            >
              <SettingIcon />
            </button>
            {/* 🔑 第一層節點不顯示刪除按鈕 */}
            {!isFirstLevel && (
              <button
                onClick={() => onDeleteNode(node.id)}
                className="p-1.5 sm:p-2 bg-red-600 hover:bg-red-700 rounded text-white transition-colors"
                title="刪除"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        {/* 設備詳細資訊 */}
        {node.fieldsValues && (
          <div
            className={`w-full mt-3 pt-3 border-t border-white/10 ${
              isFirstLevel ? "" : "ml-4"
            }`}
          >
            <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center gap-2 text-xs sm:text-sm">
              {Object.entries(node.fieldsValues).map(
                ([fieldKey, fieldValue]) => {
                  const displayLabel = fieldLabels[fieldKey] || fieldKey; // 獲取中文標籤，如果沒有則使用原始key
                  const displayValue = fieldValue || ""; // 直接使用值

                  return (
                    displayValue && (
                      <div
                        key={fieldKey}
                        className="flex flex-col sm:flex-row sm:items-center gap-1"
                      >
                        <span className="text-gray-300 whitespace-nowrap">
                          {displayLabel}
                        </span>
                        <span className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs sm:text-sm flex-1 min-h-[24px] flex items-center">
                          {displayValue}
                        </span>
                      </div>
                    )
                  );
                }
              )}
            </div>
          </div>
        )}

        {/* 顯示可放置指示 */}
        {isActiveDropTarget && (
          <div className="absolute inset-0 border-2 border-dashed border-green-400 rounded-lg pointer-events-none animate-pulse" />
        )}
      </div>

      {/* 子節點（展開才顯示） */}
      {isNodeExpanded(node.id) && hasChildren && (
        <div className="ml-2 sm:ml-4">
          <SortableContext
            items={node.children.map((child) => child.id)}
            strategy={verticalListSortingStrategy}
          >
            {node.children.map((child) => (
              <SortableTreeNode
                key={child.id}
                node={child}
                level={level + 1}
                onToggleExpand={onToggleExpand}
                isNodeExpanded={isNodeExpanded}
                onAddChild={onAddChild}
                onDeleteNode={onDeleteNode}
                onEditNode={onEditNode}
                activeId={activeId}
                overId={overId}
                isRoot={false} // 子節點都不是根節點
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
}
